const suffixes = ['', 'k', 'm', 'b', 't']

/**
 * Returns abbreviated number with unit suffix (e.g. 1100 -> 1.1k).
 *
 * @param value - The number to abbreviate
 * @param uppercase - Whether to render suffix in uppercase (default: false)
 * @returns Abbreviated string representation
 *
 * @example
 *
 * abbreviate(1234) // "1.2k"
 * abbreviate(1234, true) // "1.2K"
 * abbreviate(1234567) // "1.2m"
 * abbreviate(1234567, true) // "1.2M"
 * abbreviate(999) // "999"
 */
const abbreviate = (value: number, uppercase: boolean = false): string => {
	let shortValue = Math.abs(value)
	let suffixIndex = 0

	if (value === Infinity || value === -Infinity) {
		return `∞`
	}

	while (shortValue >= 1000) {
		shortValue /= 1000
		suffixIndex += 1
	}

	// We are limited in space, so we want our abbreviated number to have small footprint. To achieve that, we omit
	// the fractional part if integer part has more than two digits. E.g. "23 100" will appear as "23.1k", but
	// "230 100" will be shown as "230k".
	if (shortValue / 100 > 1) {
		// Integer part is too lengthy, drop the fractional part.
		shortValue = Math.round(shortValue)
	} else {
		// We are interested in only one decimal place, so we need to round it properly.
		// parseFloat() is used to remove insignificant trailing zeros.
		shortValue = parseFloat(shortValue.toFixed(1))
	}

	const suffix = suffixes[suffixIndex] ?? '...'
	const formattedSuffix = uppercase ? suffix.toUpperCase() : suffix

	return (value < 0 ? '-' : '') + shortValue.toString() + formattedSuffix
}

export default abbreviate
